import { Dialog, DialogTitle, DialogContent, Button } from '@mui/material';
import { React, useState, useMemo, useEffect } from 'react';
import { useMyContext } from '../../../context/MyContext';
import { RiMoneyRupeeCircleFill } from "react-icons/ri";
import { useNavigate } from 'react-router-dom';
import { IoMdArrowRoundBack } from "react-icons/io";
import '../../../css/custom-scrollbar.css';
import { IoIosCloseCircle } from "react-icons/io";
import { CalculatorIcon, Check, Lock, Truck, Zap, PackagePlus, Rocket, Sparkles, TrendingDown } from "lucide-react";

const slabs = [
  {
    id: 1,
    weight: "250 gm",
    label: "Light",
    gradient: "from-purple-500 to-indigo-500",
    maxGm: 250,
  },
  {
    id: 2,
    weight: "500 gm",
    label: "Standard",
    gradient: "from-teal-500 to-cyan-600",
    maxGm: 500,
  },
  {
    id: 3,
    weight: "1 Kg",
    label: "Bulk",
    gradient: "from-pink-500 to-red-600",
    maxGm: 1000,
  },
  {
    id: 4,
    weight: "5 kg",
    label: "Heavy",
    gradient: "from-green-400 to-emerald-600",
    maxGm: 5000,
  },
  {
    id: 5,
    weight: "10 kg",
    label: "Dense",
    gradient: "from-orange-400 to-yellow-500",
    maxGm: 10000,
  },
];

const getSlabIdFromWeight = (weightInGrams) => {
  if (!weightInGrams && weightInGrams !== 0) return null;
  const match = slabs.find((slab) => weightInGrams <= slab.maxGm);
  return match ? match.id : slabs[slabs.length - 1].id;
};

const MODE_CONFIG = {
  surface: { label: "Surface", icon: Truck },
  express: { label: "Express", icon: Zap },
  surface_per500: { label: "Surface +500g", icon: PackagePlus },
  express_per500: { label: "Express +500g", icon: Rocket },
};

const CourierLogo = ({ courier, isDarkMode }) => {
  const key = courier?.toLowerCase();

  if (key === "delhivery") {
    return (
      <img
        src={isDarkMode ? "/assets/images/DELHIVERYWhite.png" : "/assets/images/Delhivery.png"}
        alt="Delhivery"
        className="h-6 w-14 object-contain"
      />
    );
  }
  if (key === "ecom") {
    return (
      <img
        src={isDarkMode ? "/assets/images/logoecom.svg" : "/assets/images/ecom-express-logo.png"}
        alt="Ecom Express"
        className="h-6 w-14 object-contain"
      />
    );
  }
  if (key === "ekart") {
    return (
      <img
        src="/assets/images/ekart.png"
        alt="Ekart"
        className="h-6 w-14 object-contain"
      />
    );
  }
  if (key === "xpressbees") {
    return (
      <img
        src="/assets/images/xpressbess.png"
        alt="XpressBees"
        className="h-6 w-14 object-contain"
      />
    );
  }
  if (key === "bluedart") {
    return (
      <img
        src="/assets/images/bluedart.png"
        alt="Bluedart"
        className="h-6 w-14  object-contain"
      />
    );
  }
  if (key === "pickupxpress") {
    return (
      <img
        src="/assets/images/logo1.png"
        alt="Pickupxpress"
        className="h-6 w-14 object-contain"
      />
    );
  }
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200">
      {courier}
    </span>
  );
};

const ViewRateForword = ({ isopen, onClose, data }) => {
  const { isDarkMode } = useMyContext();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const [selectedRowId, setSelectedRowId] = useState(null);
  const [selectedRowData, setSelectedRowData] = useState([]);
  const userData = JSON.parse(localStorage.getItem('user')) || {};

  const lockedSlabId = useMemo(() => getSlabIdFromWeight(data?.requestWeight), [data]);

  const [active, setActive] = useState(lockedSlabId || 1);

  const [activeMode, setActiveMode] = useState({});

  useEffect(() => {
    if (lockedSlabId) {
      setActive(lockedSlabId);
    }
  }, [lockedSlabId]);

  useEffect(() => {
    if (data?.data?.partners_pricing) {
      const defaults = {};
      Object.keys(data.data.partners_pricing).forEach((partner) => {
        defaults[partner] = 'surface';
      });
      setActiveMode(defaults);
    }
  }, [data]);

  const handleNavigation = () => {
    navigate('/shipments/forward');
  };

  const handleClose = () => {
    onClose();
  };

  const partnersData = useMemo(() => {
    if (!data?.data?.partners_pricing) return [];

    return Object.keys(data.data.partners_pricing).map((partner) => ({
      courier: partner,
      zone: data.data.zone,
      modes: data.data.partners_pricing[partner] || {},
    }));
  }, [data]);

  const getActiveMode = (courier) => activeMode[courier] || 'surface';

  const setModeFor = (courier, mode) => {
    setActiveMode((prev) => ({ ...prev, [courier]: mode }));
  };

  // Find lowest price partner for "best deal" badge
  const bestDealCourier = useMemo(() => {
    if (!partnersData.length) return null;
    let best = null;
    let lowest = Infinity;
    partnersData.forEach(({ courier, modes }) => {
      const mode = activeMode[courier] || 'surface';
      const pricing = modes[mode];
      if (!pricing) return;
      const total = pricing.cod_total_price !== 0 ? pricing.cod_total_price : pricing.total_price;
      const num = parseFloat(String(total).replace(/[^0-9.]/g, ''));
      if (!isNaN(num) && num < lowest) {
        lowest = num;
        best = courier;
      }
    });
    return best;
  }, [partnersData, activeMode]);

  return (
    <>
      <Dialog
        open={isopen}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        fullScreen={window.innerWidth < 640}
        PaperProps={{
          sx: {
            borderRadius: { xs: '0px', sm: '24px' },
            margin: { xs: 0, sm: 'auto' },
            height: { xs: '100%', sm: '92vh' },
            maxHeight: { xs: '100%', sm: '92vh' },
            overflow: 'hidden',
            backgroundColor: isDarkMode ? '#0b0b14' : '#ffffff',
            backgroundImage: isDarkMode
              ? 'linear-gradient(135deg, #0b0b14 0%, #0d0d18 50%, #0b0b14 100%)'
              : 'none',
            boxShadow: isDarkMode
              ? '0 25px 70px -15px rgba(0, 0, 0, 0.9), 0 0 0 1px rgba(99, 102, 241, 0.35), 0 0 60px rgba(79, 70, 229, 0.25)'
              : '0 20px 50px -15px rgba(0, 0, 0, 0.25)',
            border: isDarkMode ? '1px solid rgba(99, 102, 241, 0.35)' : 'none',
          }
        }}
      >
        {/* ===== PREMIUM HEADER ===== */}
        <DialogTitle
          className="relative !p-0 bg-gradient-to-r from-indigo-600 via-purple-600 to-fuchsia-600 text-white overflow-hidden"
        >
          {/* Decorative glow circles */}
          <div className="pointer-events-none absolute -top-16 -right-10 w-48 h-48 bg-white/10 rounded-full blur-3xl" />
          <div className="pointer-events-none absolute -bottom-16 -left-10 w-48 h-48 bg-fuchsia-400/20 rounded-full blur-3xl" />

          <div className="relative z-10 px-4 sm:px-6 py-4 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-2xl bg-white/20 backdrop-blur-md flex items-center justify-center flex-shrink-0 shadow-lg shadow-purple-900/30">
                <CalculatorIcon className="text-white" size={20} />
              </div>
              <div className="min-w-0">
                <h2 className="text-base sm:text-lg font-bold flex items-center gap-1.5 truncate">
                  Rate Calculator
                  <Sparkles size={14} className="text-yellow-300 flex-shrink-0" />
                </h2>
                <p className="text-[10px] sm:text-xs text-white/80 truncate">
                  Compare & choose the best courier partner
                </p>
              </div>
            </div>

            <button
              onClick={handleClose}
              aria-label="Close"
              className="w-9 h-9 sm:w-8 sm:h-8 flex items-center justify-center rounded-full bg-white/15 hover:bg-white/30 backdrop-blur-md transition-all hover:scale-110 flex-shrink-0"
            >
              <IoIosCloseCircle size={20} className="text-white" />
            </button>
          </div>
        </DialogTitle>

        {/* ===== WEIGHT SLAB SELECTOR ===== */}
        <div className={`w-full px-3 sm:px-4 pt-3 pb-2 ${isDarkMode ? 'bg-[#0b0b14]' : 'bg-white'} border-b ${isDarkMode ? 'border-indigo-500/20' : 'border-slate-100'}`}>
          <div className="flex items-center gap-1.5 mb-2">
            <TrendingDown size={13} className="text-purple-600 dark:text-purple-300" />
            <h2 className={`text-[11px] sm:text-xs font-bold uppercase tracking-wider ${isDarkMode ? 'text-indigo-200' : 'text-slate-600'}`}>
              Select Weight Slab
            </h2>
          </div>

          <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
            {slabs.map((item) => {
              const isActive = active === item.id;
              const isDisabled = lockedSlabId ? item.id !== lockedSlabId : false;

              return (
                <div
                  key={item.id}
                  onClick={() => {
                    if (isDisabled) return;
                    setActive(item.id);
                  }}
                  className={`relative rounded-xl px-2 py-2.5 sm:py-2 text-center transition-all duration-300 border-2 overflow-hidden group
                    ${isDisabled ? "cursor-not-allowed opacity-40" : "cursor-pointer hover:-translate-y-0.5"}
                    ${isActive
                      ? `bg-gradient-to-br ${item.gradient} text-white border-transparent shadow-lg shadow-purple-500/25`
                      : isDarkMode
                        ? "bg-indigo-500/10 border-indigo-500/25 text-indigo-100 hover:border-indigo-400/50"
                        : "bg-slate-50 border-slate-200 text-slate-700 hover:border-slate-400"
                    }`}
                >
                  {isActive && (
                    <div className="absolute top-1 right-1 w-4 h-4 bg-white rounded-full flex items-center justify-center shadow-sm">
                      <Check size={10} className="text-purple-600" />
                    </div>
                  )}

                  {isDisabled && (
                    <div className="absolute top-1 right-1 text-slate-400">
                      <Lock size={11} />
                    </div>
                  )}

                  <h3 className="text-xs sm:text-sm font-bold leading-tight">{item.weight}</h3>
                  <p className={`text-[9px] sm:text-[10px] mt-0.5 ${isActive ? 'text-white/80' : isDarkMode ? 'text-indigo-300/60' : 'text-slate-400'}`}>
                    {item.label}
                  </p>
                </div>
              );
            })}
          </div>
        </div>

        {/* ===== CONTENT: COURIER CARDS ===== */}
        <DialogContent
          className={`custom-scrollbar flex-1 !p-3 sm:!p-4 ${isDarkMode ? "bg-[#0b0b14] text-white" : "bg-slate-50"}`}
          sx={{
            overflowY: 'auto',
            '&::-webkit-scrollbar': { width: '6px' },
            '&::-webkit-scrollbar-track': { background: 'transparent' },
            '&::-webkit-scrollbar-thumb': {
              background: isDarkMode ? 'rgba(99, 102, 241, 0.5)' : 'rgba(0,0,0,0.15)',
              borderRadius: '3px',
            },
          }}
        >
          {partnersData.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
              {partnersData.map(({ courier, zone, modes }) => {
                const mode = getActiveMode(courier);
                const pricing = modes[mode];
                const isBestDeal = bestDealCourier === courier;

                return (
                  <div
                    key={courier}
                    className={`relative rounded-2xl border overflow-hidden flex flex-col transition-all duration-300 hover:shadow-xl
                      ${isBestDeal
                        ? isDarkMode
                          ? 'border-emerald-400/50 bg-gradient-to-br from-[#0b0b14] via-[#0d1a14] to-[#0b0b14] shadow-[0_0_30px_rgba(16,185,129,0.2)]'
                          : 'border-emerald-300 bg-gradient-to-br from-white via-emerald-50/40 to-white shadow-lg shadow-emerald-100'
                        : isDarkMode
                          ? 'border-indigo-500/25 bg-gradient-to-br from-[#0b0b14] via-[#0d0d18] to-[#0b0b14]'
                          : 'border-slate-200 bg-white shadow-sm'
                      }`}
                  >
                    {/* Best Deal badge */}
                    {isBestDeal && (
                      <div className="absolute top-0 right-0 z-20">
                        <div className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white text-[9px] font-bold px-2.5 py-1 rounded-bl-xl shadow-md flex items-center gap-1">
                          <Sparkles size={9} />
                          BEST DEAL
                        </div>
                      </div>
                    )}

                    {/* Header: logo + zone */}
                    <div className={`flex items-center justify-between px-3 py-2.5 border-b ${isDarkMode ? 'border-indigo-500/15 bg-white/[0.02]' : 'border-slate-100 bg-slate-50/60'}`}>
                      <CourierLogo courier={courier} isDarkMode={isDarkMode} />
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${
                        isDarkMode
                          ? 'bg-indigo-500/15 border-indigo-500/40 text-indigo-200'
                          : 'bg-purple-50 border-purple-200 text-purple-700'
                      }`}>
                        Zone {zone || "N/A"}
                      </span>
                    </div>

                    {/* Mode chips */}
                    <div className="grid grid-cols-2 gap-1.5 px-2.5 pt-2.5">
                      {Object.keys(MODE_CONFIG).map((modeKey) => {
                        const isAvailable = !!modes[modeKey];
                        const isActiveChip = mode === modeKey;
                        const Icon = MODE_CONFIG[modeKey].icon;

                        return (
                          <button
                            key={modeKey}
                            type="button"
                            onClick={() => isAvailable && setModeFor(courier, modeKey)}
                            disabled={!isAvailable}
                            className={`flex items-center justify-center gap-1 text-[10px] font-semibold py-1.5 px-1.5 rounded-lg border transition-all leading-tight
                              ${isActiveChip
                                ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white border-transparent shadow-md shadow-purple-500/25'
                                : isDarkMode
                                  ? 'bg-indigo-500/10 text-indigo-200 border-indigo-500/25 hover:bg-indigo-500/20'
                                  : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-100 hover:border-slate-300'}
                              ${!isAvailable ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}`}
                          >
                            <Icon size={11} />
                            <span className="truncate">{MODE_CONFIG[modeKey].label}</span>
                          </button>
                        );
                      })}
                    </div>

                    {/* Price breakdown */}
                    <div className="px-3 py-2.5 mt-2 space-y-1.5 text-[11px] border-t border-dashed dark:border-indigo-500/15 border-slate-100">
                      <div className="flex justify-between">
                        <span className={isDarkMode ? 'text-indigo-300/70' : 'text-slate-500'}>Unit Price</span>
                        <span className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>
                          {pricing?.unit_price ? `₹ ${pricing.unit_price}` : "N/A"}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className={isDarkMode ? 'text-indigo-300/70' : 'text-slate-500'}>Base Price</span>
                        <span className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>
                          {pricing?.base_price !== undefined ? `₹ ${pricing.base_price}` : "N/A"}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className={isDarkMode ? 'text-indigo-300/70' : 'text-slate-500'}>Calc. Weight</span>
                        <span className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>
                          {pricing?.calculated_weight !== undefined ? `${pricing.calculated_weight} gm` : "N/A"}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className={isDarkMode ? 'text-indigo-300/70' : 'text-slate-500'}>COD Extra</span>
                        <span className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>
                          {pricing?.cod_extra ? `₹ ${pricing.cod_extra}` : "₹ 0"}
                        </span>
                      </div>

                      {/* Total */}
                      <div className={`flex justify-between items-center pt-2 mt-1 border-t ${isDarkMode ? 'border-indigo-500/20' : 'border-slate-200'}`}>
                        <span className={`font-bold text-xs ${isDarkMode ? 'text-indigo-200' : 'text-slate-700'}`}>
                          Total
                        </span>
                        <span className={`font-black text-base bg-gradient-to-r bg-clip-text text-transparent ${
                          isBestDeal
                            ? 'from-emerald-600 to-teal-600'
                            : 'from-purple-600 to-indigo-600'
                        }`}>
                          {pricing
                            ? `₹ ${pricing.cod_total_price !== 0 ? pricing.cod_total_price : pricing.total_price}`
                            : "N/A"}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className={`flex flex-col items-center justify-center py-16 ${isDarkMode ? 'text-indigo-300/60' : 'text-slate-400'}`}>
              <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-3 ${isDarkMode ? 'bg-indigo-500/10' : 'bg-slate-100'}`}>
                <CalculatorIcon size={28} className={isDarkMode ? 'text-indigo-400/50' : 'text-slate-300'} />
              </div>
              <p className="text-sm font-semibold">No pricing data available</p>
              <p className="text-xs mt-1">Please try again with different parameters</p>
            </div>
          )}
        </DialogContent>

        {/* ===== FOOTER ===== */}
        <div className={`px-3 sm:px-4 py-3 border-t flex justify-center sm:justify-end ${isDarkMode ? 'bg-[#0b0b14] border-indigo-500/20' : 'bg-white border-slate-100'}`}>
          <button
            onClick={handleNavigation}
            className={`w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl font-bold text-xs transition-all hover:-translate-y-0.5 shadow-sm
              ${isDarkMode
                ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white hover:from-indigo-500 hover:to-purple-500 shadow-lg shadow-indigo-900/50'
                : 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white hover:from-purple-700 hover:to-indigo-700 shadow-md shadow-purple-200'
              }`}
          >
            <IoMdArrowRoundBack size={14} />
            Create Forward Order
          </button>
        </div>
      </Dialog>
    </>
  );
};

export default ViewRateForword;